import { useMemo } from 'react';

import { Badge, Divider, FlexAligner, LoadingBubbles } from '@scaleaq/scaleui';
import { FeedingDeviceInfo, FeedingState } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { EnvironmentSensorValuesOnVideo } from '@/pages/penViewer/styles.ts';
import { colors, formatGPerSecondToKgPerMinute, formatGToKg, responsive, round } from '@/utils';

import { StyledVideoFooter } from './cameraStreamWidget.styles';

type FeedingStatusProps = {
  feedingStatus?: FeedingDeviceInfo;
  loading?: boolean;
};
const loadingZIndexDefault = 10;

export const FeedStatusOverlay = ({ feedingStatus, loading = false }: FeedingStatusProps) => {
  const { t } = useTranslation(['cameraControl', 'landingPage']);

  const amountFed = feedingStatus?.amountFed;
  const targetPlanned = feedingStatus?.targetPlanned;
  const pressureReading = feedingStatus?.pressure;
  const pressure = pressureReading != null ? round(pressureReading, 2) : undefined;
  const feedingState = feedingStatus?.feedingState;
  const isFeeding = feedingState === FeedingState.Feeding;

  const feedingStateLabel = useMemo(() => {
    switch (feedingState) {
      case FeedingState.Stopped:
        return t('landingPage:feedStatus.state.stopped');
      case FeedingState.Feeding:
        return t('landingPage:feedStatus.state.feeding');
      case FeedingState.Waiting:
        return t('landingPage:feedStatus.state.waiting');
      case FeedingState.Disabled:
        return t('landingPage:feedStatus.state.disabled');
      case FeedingState.Blowing:
        return t('landingPage:feedStatus.state.blowing');
      default:
        return t('landingPage:feedStatus.state.unknown');
    }
  }, [feedingState, t]);

  const intensityLabel = formatGPerSecondToKgPerMinute(feedingStatus?.currentIntensity);
  const amountLabel = formatGToKg(amountFed);
  const targetLabel = formatGToKg(targetPlanned);

  if (!loading && !feedingStatus) {
    return null;
  }

  return (
    <StyledVideoFooter
      gap={responsive.spacing['3xlarge']}
      alignment='center'
      $zIndex={loadingZIndexDefault + 2}
      $isFeeding={isFeeding || loading}
    >
      <FlexAligner
        gap={responsive.spacing.medium}
        alignment='center'
        style={{ width: '100%', minWidth: 0 }}
      >
        {feedingStatus && (
          <>
            <Badge label={feedingStateLabel} type={isFeeding ? 'success' : 'info'} />
            <EnvironmentSensorValuesOnVideo type='body_large' style={{ flexShrink: 0 }}>
              {amountLabel}/{targetLabel} {t('feedStatus.unit.kilograms')}
            </EnvironmentSensorValuesOnVideo>
            <Divider
              orientation='vertical'
              width={'1rem'}
              color={colors.stroke.neutral.solid[100]}
            />
            <EnvironmentSensorValuesOnVideo type='body_large' style={{ flexShrink: 0 }}>
              {intensityLabel} {t('feedStatus.unit.kilogramsPerMinute')}
            </EnvironmentSensorValuesOnVideo>
            {pressure !== undefined && (
              <>
                <Divider
                  orientation='vertical'
                  width={'1rem'}
                  color={colors.stroke.neutral.solid[100]}
                />
                <EnvironmentSensorValuesOnVideo type='body_large' style={{ flexShrink: 0 }}>
                  {pressure} {t('feedStatus.unit.bar')}
                </EnvironmentSensorValuesOnVideo>
              </>
            )}
          </>
        )}
        {loading && <LoadingBubbles size={'small'} />}
      </FlexAligner>
    </StyledVideoFooter>
  );
};
